import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Package, Network, Star, Gamepad2, History,
  ArrowDownToLine, ArrowLeftRight, User, LogOut, Shield, Diamond, HandCoins,
  Menu, X, QrCode
} from "lucide-react";

const userNav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/packages", label: "Packages", icon: Package },
  { to: "/app/fund-request", label: "Fund Request", icon: QrCode },
  { to: "/app/network", label: "My Network", icon: Network },
  { to: "/app/star-club", label: "Global Club", icon: Star },
  { to: "/app/star-club-new", label: "Star Club", icon: Star },
  { to: "/app/play-card", label: "Play Card", icon: Gamepad2 },
  { to: "/app/donation-club", label: "Donation Club", icon: HandCoins },
  { to: "/app/income", label: "Income History", icon: History },
  { to: "/app/withdraw", label: "Withdraw", icon: ArrowDownToLine },
  { to: "/app/transfer", label: "Fund Transfer", icon: ArrowLeftRight },
  { to: "/app/profile", label: "Profile", icon: User },
];

function NavList({ isAdmin, onNavigate }) {
  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${
      isActive
        ? "bg-[#D4AF37]/10 text-[#F3E5AB] border-l-2 border-[#D4AF37]"
        : "text-neutral-400 hover:bg-white/5 hover:text-white"
    }`;
  return (
    <>
      {userNav.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={linkClass}
        >
          <Icon size={16} /> {label}
        </NavLink>
      ))}
      {isAdmin && (
        <>
          <div className="overline px-3 mt-6 mb-1">Admin</div>
          <NavLink to="/admin" onClick={onNavigate} data-testid="nav-admin" className={linkClass}>
            <Shield size={16} /> Admin Panel
          </NavLink>
        </>
      )}
    </>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user && user.role === "admin";
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const SidebarInner = (
    <>
      <Link to="/" className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
        <Diamond className="text-[#D4AF37]" size={22} />
        <div>
          <div className="font-display text-xl leading-none">LT Global</div>
          <div className="overline">Club</div>
        </div>
      </Link>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        <NavList isAdmin={isAdmin} onNavigate={() => setDrawerOpen(false)} />
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="text-sm">
          <div className="font-medium truncate">{user?.full_name || "—"}</div>
          <div className="overline mt-0.5">ID {user?.user_id}</div>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className="mt-3 flex items-center gap-2 text-sm text-neutral-400 hover:text-white"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Desktop sidebar — hidden on small screens */}
      <aside className="hidden md:flex w-64 border-r border-white/10 bg-[#0d0d0d] sticky top-0 h-screen flex-col shrink-0">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-drawer-backdrop"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <aside
            className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-[#0d0d0d] border-r border-white/10 flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-drawer"
          >
            {SidebarInner}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <div className="glass-header h-14 flex items-center justify-between gap-3 px-4 md:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 -ml-2 rounded hover:bg-white/5"
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <div className="overline truncate hidden sm:block">LT Global Club · Member Portal</div>
            <div className="overline sm:hidden">LT Global</div>
          </div>
          <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap justify-end shrink-0">
            {user?.package ? <span className="gold-text">{user.package}</span> : <span className="hidden sm:inline">No active package</span>}
            {user?.current_rank && <span className="hidden md:inline">· Rank: <span className="gold-text">{user.current_rank}</span></span>}
            {user?.star_club > 0 && <span>· Club {user.star_club}</span>}
          </div>
        </div>
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">{children}</div>
      </main>

      {/* Close button inside drawer (top right) */}
      {drawerOpen && (
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="fixed top-3 right-4 z-50 md:hidden p-2 rounded bg-white/10 text-white hover:bg-white/20"
          data-testid="mobile-drawer-close"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}
